<template lang="pug">
div
    //- list of counted stock
    .box
        .box-header
            h3 Stocks to Purchase
            small Stock quantity will be updated when mark as received

        //- form to validate stock quantity
        form
            v-client-table(
                :data="stocks",
                :columns="columns",
                :options="options",
                ref="datatable",
            )
                template(slot="order_quantity" slot-scope="props")
                    div(v-if="canEdit && isOpen")
                        input.w-sm.form-control(
                            :class="{ 'has-error': errors.has('order quantity', `order_quantity_${props.row.stock_id}`) }",
                            type="text",
                            name="order quantity",
                            autocomplete="off",
                            placeholder="Enter Qty",
                            v-validate="`required|decimal:2|min_value:0`",
                            :data-vv-scope="`order_quantity_${props.row.stock_id}`",
                            :id="`order_quantity_${props.row.stock_id}`",
                            :key="`order_quantity_${props.row.stock_id}`",
                            @input="updatePurchaseStock({stock_id: props.row.stock_id, key: 'order_quantity', value: $event.target.value})",
                            :value="props.row.order_quantity"
                        )
                        span.text-danger(v-if="errors.has('order quantity', `order_quantity_${props.row.stock_id}`)")
                            | {{ errors.first('order quantity', `order_quantity_${props.row.stock_id}`) }}

                    span(v-else) {{ props.row.order_quantity  }}
            
                template(slot="received_quantity" slot-scope="props")
                    div(v-if="canEdit && isOrdered")
                        input.w-sm.form-control(
                            :class="{ 'has-error': errors.has('receive quantity', `received_quantity_${props.row.stock_id}`) }",
                            type="text",
                            name="receive quantity",
                            autocomplete="off",
                            placeholder="Enter Qty",
                            v-validate="`required|decimal:2|min_value:0`",
                            :data-vv-scope="`received_quantity_${props.row.stock_id}`",
                            :id="`received_quantity_${props.row.stock_id}`",
                            :key="`received_quantity_${props.row.stock_id}`",
                            @input="updatePurchaseStock({stock_id: props.row.stock_id, key: 'received_quantity', value: $event.target.value})",
                            :value="receivedQtyValue(props.row)"
                        )
                        span.text-danger(v-if="errors.has('receive quantity', `received_quantity_${props.row.stock_id}`)")
                            | {{ errors.first('receive quantity', `received_quantity_${props.row.stock_id}`) }}

                    span(v-else) {{ props.row.received_quantity  }}

                template(slot="price" slot-scope="props")
                    div(v-if="canEdit")
                        input.w-sm.form-control(
                            v-show="canEdit",
                            :class="{ 'has-error': errors.has('price', `price_${props.row.stock_id}`) }",
                            type="text",
                            name="price",
                            autocomplete="off",
                            placeholder="Enter Price",
                            v-validate="'required|decimal:2|min_value:0'",
                            :data-vv-scope="`price_${props.row.stock_id}`",
                            :id="`price_${props.row.stock_id}`",
                            :key="`price_${props.row.stock_id}`",
                            @input="updatePurchaseStock({stock_id: props.row.stock_id, key: 'price', value: $event.target.value})",
                            :value="props.row.price"
                        )
                        span.text-danger(v-if="errors.has('price', `price_${props.row.stock_id}`)")
                            | {{ errors.first('price', `price_${props.row.stock_id}`) }}

                    span(v-else) {{ formattedNumber(props.row.price)  }}

                template(slot="total" slot-scope="props")
                    span {{ total(props.row) }}

                template(slot="action", slot-scope="props")
                    button.md-btn.md-flat.m-b-sm.btn-xs.text-muted(
                        v-if="canEdit",
                        type="button",
                        @click="removePurchaseStock(props.row.stock_id)"
                    )
                        i.material-icons delete
                </button>

                template(slot="appendBody")
                    tr
                        td.text-right._500(:colspan="isOrdered ? 7 : 6") Total
                        td.text-center._500 {{ formattedNumber(totalPrice) }}
                        td(v-if="isOpen")
                
                template(slot="afterTable")
                    .row.p-a(v-if="isOpen && canEdit")
                        .col-md-6.col-md-offset-6
                            .form-group
                                label.m-b-0._500 Select Stock to Purchase
                                stock-inventory-selector(
                                    placeholder="Select stock",
                                    :outlet-id="purchaseOrder.outlet_id",
                                    @select="onSelectStock"
                                )
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import purchaseOrderMixin from './purchase-order-mixin';

export default {
    inject: ['$validator'],

    mixins: [purchaseOrderMixin],

    data() {
        return {
            checkedAll: false,
            options: {
                filterable: false,
                sortable: [],
                perPage: 1000,
                perPageValues: [1000],
                headings: {
                    price: 'Price',
                    current_quantity: 'Current Qty',
                    order_quantity: 'Order Qty',
                    received_quantity: 'Received Qty',
                    action: '',
                },
                columnsClasses: {
                    action: 'w-xs',
                    current_quantity: 'w-sm text-center',
                    order_quantity: 'w-sm text-center',
                    received_quantity: 'w-sm text-center',
                    price: 'w-sm text-center',
                    total: 'text-nowrap w-sm text-center',
                },
                templates: {
                    name: (h, row) => {
                        return row.stock.name;
                    },
                    sku: (h, row) => {
                        return row.stock.sku;
                    },
                    barcode: (h, row) => {
                        return row.stock.barcode;
                    },
                },
                skin: 'table m-b-0 b-b',
                uniqueKey: 'stock_id',
            },
        };
    },

    computed: {
        ...mapGetters('purchaseOrder', [
            'purchaseOrder',
            'stocks',
            'hasFinalized',
            'findStockIndex',
            'totalPrice',
        ]),

        columns() {
            const columns = [
                'name',
                'sku',
                'barcode',
                'current_quantity',
                'order_quantity',
                'received_quantity',
                'price',
                'total',
                'action',
            ];

            if (this.isOpen) {
                return columns.filter(c => c !== 'received_quantity');
            }

            if (this.isOrdered) {
                return columns.filter(c => c !== 'action');
            }
            if (this.hasFinalized) {
                return columns.filter(
                    c => c !== 'current_quantity' && c !== 'action',
                );
            }

            return columns;
        },
    },

    methods: {
        ...mapActions('purchaseOrder', [
            'removePurchaseStock',
            'addPurchaseStock',
            'updatePurchaseStock',
        ]),

        total(stock) {
            const price = parseFloat(stock.price);
            const quantity = parseFloat(stock.order_quantity);

            if (_.isNaN(price) || _.isNaN(quantity)) return 0;

            return this.formattedNumber(price * quantity);
        },

        onSelectStock(stockInventory) {
            if (this.findStockIndex(stockInventory.stock_id) >= 0) {
                $(`#order_quantity_${stockInventory.stock_id}`).focus();
            } else {
                this.addPurchaseStock(stockInventory);
            }
        },

        receivedQtyValue(stock) {
            if (_.isNull(stock.received_quantity)) {
                this.updatePurchaseStock({
                    stock_id: stock.stock_id,
                    key: 'received_quantity',
                    value: stock.order_quantity,
                });
            }

            return stock.received_quantity;
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/purchase-order/purchase-order-edit-stocks.vue